/**
 * CyclesTab.js — Manage ordering cycles: create, edit, view all cycles
 * Includes AddCycleForm and EditCyclePanel sub-components
 */

import React, { useState, useEffect, useCallback } from "react";
import S from "./adminStyles";
import { CYCLES_API } from "./adminConstants";
import { exportToCSV, printTable } from "../../utils/exportUtils";

// Converts a YYYY-MM-DD string from a date input into a local-noon ISO string.
// Prevents UTC-midnight parse shifting dates back one day in EDT.
const toLocalNoon = (dateStr) => {
  if (!dateStr) return "";
  return new Date(`${dateStr}T12:00:00`).toISOString();
};

// ── Status color helpers (module-level so EditCyclePanel can also use them) ──
function getCycleStatusColor(cycleStatus) {
  switch (cycleStatus) {
    case 'Active':    return '#16a34a'; // green
    case 'Complete':  return '#3b82f6'; // blue
    case 'Cancelled': return '#dc2626'; // red
    default:          return '#64748b'; // Pending / gray
  }
}
function getOrderStatusColor(orderStatus) {
  return orderStatus === 'Open' ? '#16a34a' : '#64748b';
}

const CYCLE_ID_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// CSS-only-style tooltip using React hover/tap state — no external library.
// The outer span is the positioned ancestor; the tooltip uses position:absolute.
function InfoIcon({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle' }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginLeft: 4, width: 14, height: 14, borderRadius: '50%',
          background: '#94a3b8', color: '#fff', fontSize: 9, fontWeight: 700,
          cursor: 'help', userSelect: 'none',
        }}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onClick={() => setVisible(v => !v)}
      >i</span>
      {visible && (
        <span style={{
          position: 'absolute',
          bottom: 'calc(100% + 4px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 210,
          background: '#1e293b',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 4,
          fontSize: 11,
          lineHeight: 1.5,
          zIndex: 100,
          whiteSpace: 'normal',
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
          {text}
        </span>
      )}
    </span>
  );
}

// ── Add Cycle Form ───────────────────────────────────────────────────
function AddCycleForm({ onAdd, onCancel, cycles = [] }) {
  const [form, setForm] = useState(() => {
    const mostRecent = [...cycles]
      .filter(c => c.pickupDate)
      .sort((a, b) => (b.pickupDate > a.pickupDate ? 1 : -1))[0];
    return {
      cycleId:        "",
      openDate:       "",
      closeDate:      "",
      pickupDate:     "",
      pickupWindows:  mostRecent?.pickupWindows  || "",
      pickupLocation: mostRecent?.pickupLocation || "",
      budgetCap:      2000,
      schoolId:       "raider-depot",
    };
  });
  const [cycleIdError, setCycleIdError] = useState('');
  const [dateError,    setDateError]    = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePickupDateChange = (e) => {
    const val = e.target.value;
    set('pickupDate', val);
    if (val) {
      set('cycleId', val);
      setCycleIdError(CYCLE_ID_PATTERN.test(val) ? '' : 'Cycle ID must follow the format YYYY-MM-DD');
    }
  };

  const handleCycleIdChange = (e) => {
    const val = e.target.value;
    set('cycleId', val);
    setCycleIdError(val && !CYCLE_ID_PATTERN.test(val) ? 'Cycle ID must follow the format YYYY-MM-DD' : '');
  };

  const isCycleIdValid = !!(form.cycleId && CYCLE_ID_PATTERN.test(form.cycleId));

  const lbl = { fontSize: "11px", color: "#64748b", fontWeight: "600" };
  const col3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: 12 };

  return (
    <div style={{ ...S.card, border: "2px solid #3b82f6" }}>
      <h3 style={{ fontSize: "14px", color: "#1e293b", margin: "0 0 12px 0" }}>
        Create New Cycle
      </h3>

      {/* Row 1: Open Date · Close Date · Pickup Date */}
      <div style={col3}>
        <div>
          <label style={lbl}>
            Open Date
            <InfoIcon text="The first day that orders can be entered for this cycle." />
          </label>
          <input style={S.input} type="date" value={form.openDate}
            onChange={(e) => set("openDate", e.target.value)} />
        </div>
        <div>
          <label style={lbl}>
            Close Date
            <InfoIcon text="The last day that orders can be entered for this cycle." />
          </label>
          <input style={S.input} type="date" value={form.closeDate}
            onChange={(e) => set("closeDate", e.target.value)} />
        </div>
        <div>
          <label style={lbl}>
            Pickup Date
            <InfoIcon text="The day of formal order pickup. Can be any day after the Close Date." />
          </label>
          <input style={S.input} type="date" value={form.pickupDate}
            onChange={handlePickupDateChange} />
        </div>
      </div>

      {/* Row 2: Cycle ID · School ID · Budget Cap */}
      <div style={col3}>
        <div>
          <label style={lbl}>
            Cycle ID
            <InfoIcon text="System identifier for the cycle. By convention this is the Pick-up Date." />
          </label>
          <input style={S.input} value={form.cycleId}
            onChange={handleCycleIdChange} placeholder="2026-04-14" />
          {cycleIdError && (
            <p style={{ color: '#dc2626', fontSize: 11, margin: '4px 0 0' }}>{cycleIdError}</p>
          )}
        </div>
        <div>
          <label style={lbl}>School ID</label>
          <input style={S.input} value={form.schoolId}
            onChange={(e) => set("schoolId", e.target.value)} />
        </div>
        <div>
          <label style={lbl}>
            Budget Cap ($)
            <InfoIcon text="The maximum value of aggregate orders for this cycle." />
          </label>
          <input style={S.input} type="number" value={form.budgetCap}
            onChange={(e) => set("budgetCap", parseInt(e.target.value) || 2000)} />
        </div>
      </div>

      {/* Row 3: Pickup Windows */}
      <div style={{ marginBottom: 12 }}>
        <label style={lbl}>Pickup Windows</label>
        <input style={S.input} value={form.pickupWindows}
          onChange={(e) => set("pickupWindows", e.target.value)}
          placeholder="Carpool 2-3 PM, After-school 3:30-3:45 PM" />
      </div>

      {/* Row 4: Pickup Location */}
      <div style={{ marginBottom: 4 }}>
        <label style={lbl}>Pickup Location</label>
        <input style={S.input} value={form.pickupLocation}
          onChange={(e) => set("pickupLocation", e.target.value)}
          placeholder="School cafeteria" />
      </div>

      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{ ...S.btnPrimary, opacity: isCycleIdValid ? 1 : 0.5, cursor: isCycleIdValid ? 'pointer' : 'not-allowed' }}
          disabled={!isCycleIdValid}
          onClick={() => {
            if (!form.openDate || !form.closeDate || !form.pickupDate ||
                form.openDate >= form.closeDate || form.closeDate >= form.pickupDate) {
              setDateError('Dates must be in order: Open → Close → Pickup, and all three are required.');
              return;
            }
            setDateError('');
            onAdd(form);
          }}
        >
          Create Cycle
        </button>
        <button style={S.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
      {dateError && (
        <p style={{ color: '#dc2626', fontSize: 12, margin: '6px 0 0' }}>{dateError}</p>
      )}
    </div>
  );
}

// ── Edit Cycle Panel ─────────────────────────────────────────────────
function EditCyclePanel({ cycle, onSave, onCancel }) {
  const [form, setForm] = useState({
    openDate: cycle.openDate ? new Date(cycle.openDate).toISOString().split('T')[0] : "",
    closeDate: cycle.closeDate ? new Date(cycle.closeDate).toISOString().split('T')[0] : "",
    pickupDate: cycle.pickupDate ? new Date(cycle.pickupDate).toISOString().split('T')[0] : "",
    pickupWindows: cycle.pickupWindows || "",
    pickupLocation: cycle.pickupLocation || "",
    budgetCap: cycle.budgetCap || 10000,
    cycleStatus: cycle.cycleStatus || 'Pending',
    orderStatus: cycle.orderStatus || 'Closed',
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div style={{ ...S.card, border: "2px solid #f59e0b" }}>
      <h3 style={{ fontSize: "14px", color: "#1e293b", margin: "0 0 12px 0" }}>
        Edit Cycle: {cycle.cycleId}
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "12px",
        }}
      >
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Open Date
          </label>
          <input
            style={S.input}
            type="date"
            value={form.openDate}
            onChange={(e) => set("openDate", e.target.value)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Close Date
          </label>
          <input
            style={S.input}
            type="date"
            value={form.closeDate}
            onChange={(e) => set("closeDate", e.target.value)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Pickup Date
          </label>
          <input
            style={S.input}
            type="date"
            value={form.pickupDate}
            onChange={(e) => set("pickupDate", e.target.value)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Budget Cap ($)
          </label>
          <input
            style={S.input}
            type="number"
            value={form.budgetCap}
            onChange={(e) => set("budgetCap", parseInt(e.target.value) || 10000)}
          />
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Cycle Status
          </label>
          <div style={{ padding: "6px 0", fontSize: "13px", fontWeight: "600", color: getCycleStatusColor(form.cycleStatus) }}>
            {form.cycleStatus || 'Pending'}
          </div>
        </div>
        <div>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Order Status
          </label>
          <div style={{ padding: "6px 0", fontSize: "13px", fontWeight: "600", color: getOrderStatusColor(form.orderStatus) }}>
            {form.orderStatus || 'Closed'}
          </div>
        </div>
        <div style={{ gridColumn: "span 3" }}>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Pickup Windows
          </label>
          <input
            style={S.input}
            value={form.pickupWindows}
            onChange={(e) => set("pickupWindows", e.target.value)}
            placeholder="Carpool 2-3 PM, After-school 3:30-3:45 PM"
          />
        </div>
        <div style={{ gridColumn: "span 3" }}>
          <label
            style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}
          >
            Pickup Location
          </label>
          <input
            style={S.input}
            value={form.pickupLocation}
            onChange={(e) => set("pickupLocation", e.target.value)}
            placeholder="School cafeteria"
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button style={S.btnPrimary} onClick={() => onSave(form)}>
          Save Changes
        </button>
        <button style={S.btnSecondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Cycles Tab ─────────────────────────────────────────────────────
export default function CyclesTab({ user }) {
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCycle, setEditCycle] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(CYCLES_API, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCycles(data.cycles || []);
      }
    } catch (err) {
      console.error("Cycles load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  const updateCycle = async (cycleId, updates) => {
    try {
      const token = await user.getIdToken();
      await fetch(`${CYCLES_API}/${cycleId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...updates,
          ...(updates.openDate   && { openDate:   toLocalNoon(updates.openDate)   }),
          ...(updates.closeDate  && { closeDate:  toLocalNoon(updates.closeDate)  }),
          ...(updates.pickupDate && { pickupDate: toLocalNoon(updates.pickupDate) }),
        }),
      });
      loadCycles();
      setEditCycle(null);
    } catch (err) {
      console.error("Update error:", err);
    }
  };

  const addCycle = async (newCycle) => {
    try {
      const token = await user.getIdToken();
      await fetch(CYCLES_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newCycle,
          openDate:    toLocalNoon(newCycle.openDate),
          closeDate:   toLocalNoon(newCycle.closeDate),
          pickupDate:  toLocalNoon(newCycle.pickupDate),
        }),
      });
      loadCycles();
      setShowAddForm(false);
    } catch (err) {
      console.error("Add error:", err);
    }
  };

  const generateSessionCode = async (cycleId) => {
    try {
      const token = await user.getIdToken();
      await fetch(`${CYCLES_API}/${cycleId}/generate-pin`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      loadCycles();
    } catch (err) {
      console.error("Session code error:", err);
    }
  };

  const handleCancelAdd = useCallback(() => setShowAddForm(false), []);
  const handleCancelEdit = useCallback(() => setEditCycle(null), []);

  useEffect(() => {
    if (!showAddForm) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelAdd();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showAddForm, handleCancelAdd]);

  useEffect(() => {
    if (!editCycle) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleCancelEdit();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editCycle, handleCancelEdit]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const raw = typeof dateStr === "string" && dateStr.length === 10
      ? `${dateStr}T12:00:00`
      : dateStr;
    const date = new Date(raw);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const CYCLES_COLS = [
    { key: "cycleId",     label: "Cycle ID" },
    { key: "cycleStatus", label: "Cycle Status" },
    { key: "orderStatus", label: "Order Status" },
    { key: "openDate",    label: "Open Date" },
    { key: "closeDate",   label: "Close Date" },
    { key: "pickupDate",  label: "Pickup Date" },
  ];

  const exportCyclesCSV = () => {
    const today = new Date().toISOString().split("T")[0];
    exportToCSV(`raider-depot-cycles-${today}.csv`, cycles, CYCLES_COLS);
  };

  const printCycles = () => {
    printTable("Cycles", `${cycles.length} cycles`, CYCLES_COLS, cycles);
  };


  return (
    <>
      <div style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#ffffff", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>
        <div style={S.flexRowBetween}>
          <h2 style={S.cardTitle}>Cycle Management</h2>
          <div style={{ display: "flex", gap: "8px" }}>
            <button style={S.btnSecondary} onClick={exportCyclesCSV} disabled={cycles.length === 0}>
              Export CSV
            </button>
            <button style={S.btnSecondary} onClick={printCycles} disabled={cycles.length === 0}>
              Print
            </button>
            <button
              style={S.btnPrimary}
              onClick={() => setShowAddForm(!showAddForm)}
            >
              {showAddForm ? "Cancel" : "+ Create Cycle"}
            </button>
          </div>
        </div>
        {!loading && (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ backgroundColor: "#ffffff" }}>
              <tr>
                <th style={{ ...S.th, width: "10%" }}>Cycle ID</th>
                <th style={{ ...S.th, width: "7%" }}>Cycle Status</th>
                <th style={{ ...S.th, width: "7%" }}>Order Status</th>
                <th style={{ ...S.th, width: "10%" }}>Open Date</th>
                <th style={{ ...S.th, width: "10%" }}>Close Date</th>
                <th style={{ ...S.th, width: "10%" }}>Pickup Date</th>
                <th style={{ ...S.th, width: "20%" }}>Pickup Location</th>
                <th style={{ ...S.th, width: "9%" }}>Budget Cap</th>
                <th style={{ ...S.th, width: "10%" }}>Session Code</th>
                <th style={{ ...S.th, width: "7%" }}>Actions</th>
              </tr>
            </thead>
          </table>
        )}
      </div>

      {showAddForm && (
        <AddCycleForm onAdd={addCycle} onCancel={handleCancelAdd} cycles={cycles} />
      )}

      {editCycle && (
        <EditCyclePanel
          cycle={cycles.find((c) => c.cycleId === editCycle)}
          onSave={(updates) => updateCycle(editCycle, updates)}
          onCancel={handleCancelEdit}
        />
      )}

      <div style={{ backgroundColor: "#fff", padding: "20px", marginBottom: "16px" }}>
        {loading ? (
          <p style={{ color: "#64748b" }}>Loading cycles...</p>
        ) : (
          <table style={{ ...S.table, tableLayout: "fixed" }}>
            <thead style={{ visibility: "hidden" }}>
              <tr>
                <th style={{ ...S.th, width: "10%" }}>Cycle ID</th>
                <th style={{ ...S.th, width: "7%" }}>Cycle Status</th>
                <th style={{ ...S.th, width: "7%" }}>Order Status</th>
                <th style={{ ...S.th, width: "10%" }}>Open Date</th>
                <th style={{ ...S.th, width: "10%" }}>Close Date</th>
                <th style={{ ...S.th, width: "10%" }}>Pickup Date</th>
                <th style={{ ...S.th, width: "20%" }}>Pickup Location</th>
                <th style={{ ...S.th, width: "9%" }}>Budget Cap</th>
                <th style={{ ...S.th, width: "10%" }}>Session Code</th>
                <th style={{ ...S.th, width: "7%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cycles.map((cycle) => (
                <tr key={cycle.cycleId}>
                  <td style={S.td}>
                    <strong>{cycle.cycleId}</strong>
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
                        fontSize: 11, fontWeight: 600, color: '#fff',
                        backgroundColor: getCycleStatusColor(cycle.cycleStatus) }}>
                      {cycle.cycleStatus || 'Pending'}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
                        fontSize: 11, fontWeight: 600, color: '#fff',
                        backgroundColor: getOrderStatusColor(cycle.orderStatus) }}>
                      {cycle.orderStatus || 'Closed'}
                    </span>
                  </td>
                  <td style={S.td}>{formatDate(cycle.openDate)}</td>
                  <td style={S.td}>{formatDate(cycle.closeDate)}</td>
                  <td style={S.td}>{formatDate(cycle.pickupDate)}</td>
                  <td style={S.td}>{cycle.pickupLocation || "—"}</td>
                  <td style={S.td}>${cycle.budgetCap || 10000}</td>
                  <td style={S.td}>
                    {cycle.sessionCode ? (
                      <span>
                        <code style={{ fontWeight: 'bold', fontFamily: 'monospace', background: '#f1f5f9', padding: '2px 6px', borderRadius: 3 }}>
                          {cycle.sessionCode}
                        </code>
                        {' '}
                        <button
                          style={{ ...S.btnSecondary, padding: '2px 8px', fontSize: '11px' }}
                          onClick={() => {
                            if (window.confirm('Revoke this session code and generate a new one?')) {
                              generateSessionCode(cycle.cycleId);
                            }
                          }}
                        >
                          Revoke
                        </button>
                      </span>
                    ) : (
                      <button
                        style={{ ...S.btnSecondary, padding: '2px 8px', fontSize: '11px' }}
                        disabled={cycle.cycleStatus === 'Complete' || cycle.cycleStatus === 'Cancelled'}
                        onClick={() => generateSessionCode(cycle.cycleId)}
                      >
                        Generate
                      </button>
                    )}
                  </td>
                  <td style={S.td}>
                    <button
                      style={{
                        ...S.btnSecondary,
                        padding: "4px 10px",
                        fontSize: "12px",
                      }}
                      onClick={() =>
                        setEditCycle(
                          editCycle === cycle.cycleId ? null : cycle.cycleId,
                        )
                      }
                    >
                      {editCycle === cycle.cycleId ? "Close" : "Edit"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}